import { defineStore } from "pinia";
import { type Ref, ref, computed } from "vue";
import { getUserProfile } from "@/modules/userProfile/service/userProfileService";
import type { UserProfile } from "@/modules/userProfile/types/UserProfile";
import type { AzureUserProfile } from "@/modules/userProfile/types/AzureUserProfile";
import { type Router, useRouter } from "vue-router";
import { USER_ROLES } from "@/modules/userProfile/types/UserProfile";
import { fromAzureUserProfile } from "@/modules/userProfile/service/service.utils";

export const useUserProfileStore = defineStore("userProfileStore", () => {
  // state
  const userProfile: Ref<UserProfile> = ref<UserProfile>({});

  const router: Router = useRouter();
  let fetchUserProfilePromise: Promise<UserProfile> | null = null;

  // getters
  const isLoggedIn = computed(() => {
    return Object.keys(userProfile.value).length > 0;
  });

  const displayUserRole = computed(() => {
    return userProfile.value.roles?.join(", ");
  });

  // actions
  function fetchUserProfile(): Promise<UserProfile> {
    if (isLoggedIn.value) {
      return Promise.resolve(userProfile.value);
    }

    if (!fetchUserProfilePromise) {
      fetchUserProfilePromise = loadUserProfile().finally(() => {
        fetchUserProfilePromise = null;
      });
    }

    return fetchUserProfilePromise;
  }

  async function loadUserProfile(): Promise<UserProfile> {
    try {
      const userAccount = await getUserProfile();
      const hasAccess = checkUserAccess(userAccount.roles);
      if (!hasAccess) {
        await router?.push({ path: "/access-denied" });
        return userProfile.value;
      }
      await setUserProfile(userAccount);
    } catch (error: any) {
      if ([401, 403].includes(error.status)) {
        await router?.push({ path: "/access-denied" });
      }
    }

    return userProfile.value;
  }

  function checkUserAccess(userRoles: Array<string>) {
    const USER_ROLES_FOR_MODULES: Array<string> = USER_ROLES;
    return userRoles.some((userRole) => USER_ROLES_FOR_MODULES.includes(userRole));
  }

  async function setUserProfile(userAccount: AzureUserProfile) {
    userProfile.value = fromAzureUserProfile(userAccount);
  }

  function isLoggedInUser(aadObjectId?: string | null): boolean {
    if (!aadObjectId) {
      return false;
    }
    return userProfile.value.id === aadObjectId;
  }

  return {
    userProfile,
    isLoggedIn,
    fetchUserProfile,
    displayUserRole,
    isLoggedInUser
  };
});
